import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ExpenseMaster = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get("/member/getexpense");
      setData(response.data.data || []);
    } catch (error) {
      message.error("Failed to fetch expenses.");
    }
  };

  const handleAdd = async (values) => {
    try {
      await api.post("/member/createexpense", values);
      message.success("Expense added successfully");
      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to add expense.");
    }
  };

  const handleUpdate = async (values) => {
    try {
      await api.put(`/member/upexpense/${currentRecord._id}`, values);
      message.success("Expense updated successfully");
      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to update expense.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/member/delexpense/${id}`);
      message.success("Expense deleted successfully");
      fetchData();
    } catch (error) {
      message.error("Failed to delete expense.");
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Expense Data", 10, 10);

    const tableColumn = ["S.No", "Expense Type", "Description", "Status"];
    const tableRows = data.map((item, index) => [index + 1, item.expensetype, item.desc, item.status]);

    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save("expense_data.pdf");
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item, index) => ({
        "S.No": index + 1,
        "Expense Type": item.expensetype,
        "Description": item.desc,
        "Status": item.status,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");
    XLSX.writeFile(workbook, "expense_data.xlsx");
  };


  const filteredData = data.filter((record) => 
    record.expensetype.toLowerCase().includes(searchText.toLowerCase()) ||
    record.desc.toLowerCase().includes(searchText.toLowerCase()) ||
    record.status.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    { title: "Expense Type", dataIndex: "expensetype" },
    { title: "Description", dataIndex: "desc" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditMode(true);
              setCurrentRecord(record);
              form.setFieldsValue(record);
              setIsModalVisible(true);
            }}
          />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row>
        <Col span={6}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search Expense"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Button icon={<FileExcelOutlined />} onClick={exportToExcel} style={{ marginRight: 8 }}>Export Excel</Button>
          <Button icon={<FilePdfOutlined />} onClick={exportToPDF} style={{ marginRight: 8 }}>Export PDF</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(false);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Expense
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />
      <Modal
        title={editMode ? "Edit Expense" : "Add Expense"}
        open={isModalVisible}
        onOk={() => form.validateFields().then((values) => (editMode ? handleUpdate(values) : handleAdd(values)))}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="Expense Type" name="expensetype" rules={[{ required: true }]}> 
            <Input placeholder="Enter Expense Type" />
          </Form.Item>
          <Form.Item label="Description" name="desc" rules={[{ required: true }]}> 
            <Input placeholder="Enter Description" />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}> 
            <Select options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]} placeholder="Select Status" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ExpenseMaster;
